import React, { useState, useEffect } from 'react';
import { Modal, Button, Group, Text, Alert, Loader, Card, Image, SimpleGrid, FileInput, Box, ActionIcon } from '@mantine/core';
import { FaUpload, FaArrowLeft, FaArrowRight, FaTimes } from 'react-icons/fa';
import { BASE_URL } from '../Redux/apiConfig';
import { FormattedMessage } from 'react-intl';
import { loadLotImageAPI } from '../Redux/properties/PropertiesAPI';

const MAX_IMAGES = 5;
const MAX_FILE_SIZE = 1024 * 1024;
const BLOB_STORAGE_URL = 'https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/images';

const ImageUploadModal = ({ modal, toggle, selectedData }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [existingImages, setExistingImages] = useState([]);
  const [reordering, setReordering] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (modal && selectedData) {
      fetchExistingImages();
    }
  }, [modal, selectedData]);

  const fetchExistingImages = async () => {
    setLoading(true);
    setError(null);
    try {
      const images = [];
      const timestamp = Date.now();
      
      for (let i = 0; i < MAX_IMAGES; i++) {
        const suffix = i === 0 ? '' : `_${i}`;
        const baseUrl = `${BLOB_STORAGE_URL}/${selectedData.productID}${suffix}.png`;
        const imageUrl = `${baseUrl}?t=${timestamp}`;
        
        try {
          const response = await fetch(imageUrl, { 
            method: 'HEAD',
            // Suppress browser console errors for 404s
            cache: 'no-store'
          });
          if (response.ok) {
            images.push({
              id: `${selectedData.productID}${suffix}`,
              url: imageUrl,
              baseUrl: baseUrl,
              originalUrl: imageUrl,
              index: i,
              originalIndex: i
            });
          }
        } catch (err) {
          // Silently ignore
        }
      }
      
      setExistingImages(images);
    } catch (err) {
      console.error('Error fetching images:', err);
      setError('Failed to load images');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (selectedFile) => {
    setError(null);

    if (!selectedFile) return;

    if (selectedFile.size > MAX_FILE_SIZE) {
      setError('File too large (max 1MB)');
      return;
    }

    if (!selectedFile.type.startsWith('image/')) {
      setError('Invalid file type');
      return;
    }

    if (existingImages.length >= MAX_IMAGES) {
      setError('Maximum 5 images');
      return;
    }

    setFile(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) {
      setError('No file selected');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const reader = new FileReader();
      
      reader.onloadend = async () => {
        const base64String = reader.result;
        
        const suffix = existingImages.length === 0 ? '' : `_${existingImages.length}`;
        const productIDWithSuffix = `${selectedData.productID}${suffix}`;

        const payload = {
          property_img: { data: base64String },
          productID: productIDWithSuffix
        };

        try {
          const response = await loadLotImageAPI(payload);
          
          console.log('Upload success:', response);

          await fetchExistingImages();
          setFile(null);
          setUploading(false);
        } catch (err) {
          console.error('Error uploading image:', err);
          setError(err.message || 'Upload failed');
          setUploading(false);
        }
      };

      reader.onerror = () => {
        setError('Failed to read file');
        setUploading(false);
      };

      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err.message || 'Upload failed');
      setUploading(false);
    }
  };

  const moveImage = async (currentIndex, direction) => {
    const newIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= existingImages.length) return;

    const reordered = [...existingImages];
    const temp = reordered[currentIndex];
    reordered[currentIndex] = reordered[newIndex];
    reordered[newIndex] = temp;

    setExistingImages(reordered);
    await handleReorder(reordered);
  };

  const handleDelete = async (indexToDelete) => {
    if (!window.confirm('Delete this image?')) return;

    setDeleting(true);
    setError(null);

    try {
      const imageToDelete = existingImages[indexToDelete];
      const remaining = existingImages.filter((_, idx) => idx !== indexToDelete);
      
      // 🔍 DEBUG: Log what we're deleting
      console.log('=== DELETE DEBUG ===');
      console.log('Deleting index:', indexToDelete);
      console.log('Image to delete:', imageToDelete);
      console.log('Remaining images:', remaining);
      console.log('===================');
      
      setExistingImages(remaining);

      const blobSuffix = imageToDelete.id.replace(selectedData.productID, '');
      const deleteUrl = `${BASE_URL}/deleteImage/${selectedData.productID}${blobSuffix}`;
      const token = localStorage.getItem('authToken');
      
      console.log(`🗑️ Deleting blob: ${selectedData.productID}${blobSuffix}.png`);
      console.log(`📍 DELETE URL: ${deleteUrl}`);
      
      try {
        const response = await fetch(deleteUrl, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        console.log(`✅ Delete response status: ${response.status}`);
        if (!response.ok && response.status !== 404) {
          console.warn(`Delete failed with status: ${response.status}`);
        }
      } catch (err) {
        console.warn('Delete request failed:', err.message);
      }

      if (remaining.length === 0) {
        setDeleting(false);
        return;
      }

      // Re-upload remaining images with sequential names
      console.log('🔄 Re-uploading remaining images...');
      const imageDataPromises = remaining.map(async (img) => {
        const response = await fetch(img.originalUrl);
        const blob = await response.blob();
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      });

      const imagesBase64 = await Promise.all(imageDataPromises);

      for (let i = 0; i < imagesBase64.length; i++) {
        const suffix = i === 0 ? '' : `_${i}`;
        console.log(`📤 Re-uploading as: ${selectedData.productID}${suffix}.png`);
        const payload = {
          property_img: { data: imagesBase64[i] },
          productID: `${selectedData.productID}${suffix}`
        };
        await loadLotImageAPI(payload);
      }

      // Clean up orphans
      const originalCount = existingImages.length;
      console.log(`🧹 Cleaning orphans from index ${remaining.length} to ${originalCount - 1}`);
      for (let i = remaining.length; i < originalCount; i++) {
        const orphanSuffix = i === 0 ? '' : `_${i}`;
        const orphanUrl = `${BASE_URL}/deleteImage/${selectedData.productID}${orphanSuffix}`;
        console.log(`🗑️ Cleaning orphan: ${selectedData.productID}${orphanSuffix}.png`);
        try {
          await fetch(orphanUrl, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
        } catch (err) {
          // Silently ignore
        }
      }

      console.log('✅ Delete operation complete, refreshing images...');
      await fetchExistingImages();
    } catch (err) {
      console.error('Error deleting image:', err);
      setError('Failed to delete image');
      await fetchExistingImages();
    } finally {
      setDeleting(false);
    }
  };

  const handleReorder = async (reorderedImages) => {
    setReordering(true);
    setError(null);

    try {
      const imageDataPromises = reorderedImages.map(async (img) => {
        const response = await fetch(img.originalUrl);
        const blob = await response.blob();
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      });

      const imagesBase64 = await Promise.all(imageDataPromises);

      for (let i = 0; i < imagesBase64.length; i++) {
        const suffix = i === 0 ? '' : `_${i}`;
        const payload = {
          property_img: { data: imagesBase64[i] },
          productID: `${selectedData.productID}${suffix}`
        };
        await loadLotImageAPI(payload);
      }

      const timestamp = Date.now();
      const finalImages = reorderedImages.map((img, idx) => {
        const suffix = idx === 0 ? '' : `_${idx}`;
        const baseUrl = `${BLOB_STORAGE_URL}/${selectedData.productID}${suffix}.png`;
        const newUrl = `${baseUrl}?t=${timestamp}`;
        return {
          ...img,
          url: newUrl,
          baseUrl: baseUrl,
          originalUrl: newUrl,
          index: idx,
          id: `${selectedData.productID}${suffix}`
        };
      });
      
      setExistingImages(finalImages);

    } catch (err) {
      console.error('Error reordering images:', err);
      setError('Failed to reorder images');
      await fetchExistingImages();
    } finally {
      setReordering(false);
    }
  };

  return (
    <Modal
      opened={modal}
      onClose={toggle}
      title={<FormattedMessage id="modal.loadimage.title" />}
      size="lg"
    >
      {error && (
        <Alert color="red" mb="md">
          {error}
        </Alert>
      )}

      {(loading || reordering || deleting) ? (
        <Box style={{ textAlign: 'center', padding: '2rem' }}>
          <Loader />
          {reordering && <Text size="sm" mt="sm">Reordering images...</Text>}
          {deleting && <Text size="sm" mt="sm">Deleting image...</Text>}
        </Box>
      ) : (
        <>
          {existingImages.length > 0 && (
            <Box mb="xl" pb="md" style={{ borderBottom: '1px solid #e0e0e0' }}>
              <Text size="sm" color="dimmed" mb="sm">
                {existingImages.length}/{MAX_IMAGES} <FormattedMessage id="modal.loadimage.images" />
                {existingImages.length > 1 && <Text span size="xs" ml="xs">(Use arrows to reorder)</Text>}
              </Text>
              
              <SimpleGrid cols={3} spacing="sm">
                {existingImages.map((image, index) => (
                  <Card key={`${image.id}-${index}`} padding="xs" shadow="sm" style={{ display: 'flex', flexDirection: 'column', minHeight: '180px', position: 'relative' }}>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      size="sm"
                      onClick={() => handleDelete(index)}
                      disabled={reordering || deleting}
                      style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        zIndex: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        borderRadius: '50%'
                      }}
                    >
                      <FaTimes size={12} />
                    </ActionIcon>
                    
                    <Card.Section style={{ flex: '0 0 120px' }}>
                      <Image
                        src={image.url}
                        height={120}
                        alt="Product"
                        fit="cover"
                      />
                    </Card.Section>
                    
                    {existingImages.length > 1 && (
                      <Card.Section p="xs" style={{ marginTop: 'auto' }}>
                        <Group spacing="xs">
                          <Button
                            variant="outline"
                            size="xs"
                            style={{ flex: 1 }}
                            disabled={index === 0 || reordering || deleting}
                            onClick={() => moveImage(index, 'left')}
                          >
                            <FaArrowLeft />
                          </Button>
                          <Button
                            variant="outline"
                            size="xs"
                            style={{ flex: 1 }}
                            disabled={index === existingImages.length - 1 || reordering || deleting}
                            onClick={() => moveImage(index, 'right')}
                          >
                            <FaArrowRight />
                          </Button>
                        </Group>
                      </Card.Section>
                    )}
                  </Card>
                ))}
              </SimpleGrid>
            </Box>
          )}

          {existingImages.length < MAX_IMAGES && (
            <Box mt="xl" pt="md">
              <Text size="sm" weight={500} mb="sm">
                Upload New Image
              </Text>
              <FileInput
                placeholder="Select image"
                accept="image/*"
                value={file}
                onChange={handleFileChange}
                disabled={uploading || deleting}
                mb="xs"
              />
              <Text size="xs" color="dimmed">
                <FormattedMessage id="modal.loadimage.hint" />
              </Text>
            </Box>
          )}
        </>
      )}

      <Group position="right" mt="xl" pt="md" style={{ borderTop: '1px solid #e0e0e0' }}>
        <Button variant="default" onClick={toggle} disabled={uploading || reordering || deleting}>
          <FormattedMessage id="button.cancel" />
        </Button>
        {existingImages.length < MAX_IMAGES && (
          <Button
            onClick={handleUpload}
            disabled={!file || uploading || reordering || deleting}
            loading={uploading}
          >
            <FaUpload style={{ marginRight: '0.5rem' }} />
            Upload
          </Button>
        )}
      </Group>
    </Modal>
  );
};

export default ImageUploadModal;